import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { hapticTap, hapticConfirm, hapticHayahSwipe } from "../hayahHaptics";

// ─── DESIGN TOKENS (matches Sirat Log — Ledger.html) ─────────────
const C = {
  ink:   '#f4eee0',
  dim:   'rgba(244,238,224,0.62)',
  faint: 'rgba(244,238,224,0.55)',
  green: '#00e676',
  amber: '#f5b342',
  coral: '#f08a6b',
  cream: '#e9dfc7',
  cyan:  '#22d3ee',
};
const FS = "'Geist', system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
const FM = "'Geist Mono', ui-monospace, 'SF Mono', Menlo, monospace";

function useCountUp(target, dur = 1400, delay = 200) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setV(Math.round((1 - Math.pow(1 - p, 3)) * target));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); cancelAnimationFrame(raf); };
  }, [target, dur, delay]);
  return v;
}

function useRingFill(pct, delay = 300, dur = 1500) {
  const [f, setF] = useState(0);
  const mountedRef = useRef(false);
  useEffect(() => {
    if (mountedRef.current) {
      setF(pct);
      return;
    }
    mountedRef.current = true;
    let raf, tid = setTimeout(() => {
      let t0;
      const step = ts => {
        if (!t0) t0 = ts;
        const p = Math.min((ts - t0) / dur, 1);
        setF((1 - Math.pow(1 - p, 3)) * pct);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delay);
    return () => { clearTimeout(tid); cancelAnimationFrame(raf); };
  }, [pct, delay, dur]);
  return f;
}

function GlowRing({ value, max, color, size = 220, sw = 13, delay = 200, uid = 'r', centerNode }) {
  const r = (size - sw) / 2, cx = size / 2, cy = size / 2, circ = 2 * Math.PI * r;
  // Base fill caps at 100% in the original colour. Overflow (value > max)
  // continues on top in red so the user can see they're past the goal.
  const ratio = max > 0 ? value / max : 0;
  // Green base animates 0→100% first; red overflow only starts after the
  // base has finished its sweep so the user reads the over-goal portion as
  // a clean second beat rather than two simultaneous animations.
  const fill = useRingFill(Math.min(ratio, 1), delay, 1700) * circ;
  const overflowFill = useRingFill(Math.min(Math.max(0, ratio - 1), 1), delay + 1700, 1700) * circ;
  const overflow = overflowFill > 4;
  const tipFill = overflow ? overflowFill : fill;
  const tipColor = overflow ? '#ff6b6b' : color;
  const endA = (tipFill / circ) * 2 * Math.PI - Math.PI / 2;
  const ex = cx + Math.cos(endA) * r, ey = cy + Math.sin(endA) * r;
  const tr = 'stroke-dasharray 1.7s cubic-bezier(0.22,0.61,0.36,1)';

  const ticks = Array.from({ length: 48 }, (_, i) => {
    const a = (i / 48) * 2 * Math.PI - Math.PI / 2, big = i % 12 === 0;
    const r1 = r + sw / 2 + 3, r2 = r + sw / 2 + (big ? 11 : 6);
    return <line key={i}
      x1={cx + Math.cos(a) * r1} y1={cy + Math.sin(a) * r1}
      x2={cx + Math.cos(a) * r2} y2={cy + Math.sin(a) * r2}
      stroke={big ? 'rgba(255,255,255,0.24)' : 'rgba(255,255,255,0.07)'}
      strokeWidth={big ? 1.5 : 0.7} />;
  });

  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: 'visible', transform: 'rotate(-90deg)' }}>
        {ticks}
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth={sw} />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke={color} strokeWidth={sw}
          strokeLinecap="round" strokeDasharray={`${fill} ${circ}`}
          style={{ transition: tr }} />
        {overflow && (
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ff6b6b" strokeWidth={sw}
            strokeLinecap="round" strokeDasharray={`${overflowFill} ${circ}`}
            style={{ transition: tr }} />
        )}
        {tipFill > 10 && (
          <circle cx={ex} cy={ey} r={sw / 2 + 1} fill={tipColor} />
        )}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {centerNode}
      </div>
    </div>
  );
}

function VertMacroBar({ label, value, max, color, barH = 178, delay = 400, unit = 'g', isHero = false, remaining, onTap }) {
  const fill = useRingFill(Math.min(value / max, 1), delay, 1500);
  const disp = useCountUp(Math.round(value), 1300, delay);
  const width = isHero ? 26 : 18;
  const tappable = typeof onTap === 'function';
  return (
    <div
      role={tappable ? 'button' : undefined}
      tabIndex={tappable ? 0 : undefined}
      aria-label={tappable ? `${label} focus` : undefined}
      onClick={tappable ? onTap : undefined}
      onKeyDown={tappable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTap(); } } : undefined}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        gap: isHero ? 10 : 7, width: isHero ? 50 : 36,
        cursor: tappable ? 'pointer' : 'default',
        WebkitTapHighlightColor: 'transparent',
        transition: 'transform 0.08s ease',
      }}
      onPointerDown={tappable ? (e) => { e.currentTarget.style.transform = 'scale(0.97)'; } : undefined}
      onPointerUp={tappable ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
      onPointerLeave={tappable ? (e) => { e.currentTarget.style.transform = 'scale(1)'; } : undefined}
    >
      <div style={{ fontFamily: FM, fontSize: isHero ? 9.5 : 8, color, textTransform: 'uppercase', letterSpacing: '1.4px', writingMode: 'vertical-rl', transform: 'rotate(180deg)', height: isHero ? 60 : 50, textAlign: 'center', fontWeight: isHero ? 600 : 400 }}>
        {label}{isHero ? ' ★' : ''}
      </div>
      <div style={{ position: 'relative', width, height: barH, background: 'rgba(255,255,255,0.06)', borderRadius: width / 2, overflow: 'hidden' }}>
        {[0.25, 0.5, 0.75].map(t => (
          <div key={t} style={{ position: 'absolute', left: 2, right: 2, top: `${(1 - t) * 100}%`, height: 1, background: 'rgba(255,255,255,0.12)' }} />
        ))}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: `${fill * 100}%`,
          background: `linear-gradient(to top, ${color}, ${color}99)`,
          transition: 'height 1.5s cubic-bezier(0.22,0.61,0.36,1)',
          boxShadow: `0 -8px 20px ${color}88`,
          borderRadius: `0 0 ${width / 2}px ${width / 2}px`,
        }}>
          {isHero && (
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to bottom, rgba(255,255,255,0.35) 0%, transparent 40%, rgba(255,255,255,0.1) 100%)`, animation: 'log-bar-shimmer 2.4s ease-in-out infinite' }} />
          )}
        </div>
        {isHero && fill > 0.1 && (
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${fill * 100}%`, height: 6, background: color, filter: 'blur(4px)', opacity: 0.8, animation: 'log-glow-pulse 1.8s ease-in-out infinite' }} />
        )}
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontFamily: FM, fontSize: isHero ? 13 : 11, color: isHero ? color : C.ink, fontWeight: 600, lineHeight: 1 }}>{disp}</div>
        <div style={{ fontFamily: FM, fontSize: 8, color: C.faint, marginTop: 2 }}>/{Math.round(max)}{unit}</div>
      </div>
      {isHero && remaining !== undefined && (
        <div style={{ padding: '3px 8px', borderRadius: 99, background: `rgba(34,211,238,0.12)`, border: `1px solid ${color}44`, fontFamily: FM, fontSize: 9, color, letterSpacing: '0.6px' }}>
          {Math.max(0, Math.round(remaining))}g left
        </div>
      )}
    </div>
  );
}

function CarbsBar({ value, max, onTap }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const fill = useRingFill(pct, 600, 1300);
  const disp = useCountUp(Math.round(value), 1200, 600);
  return (
    <div
      role={onTap ? 'button' : undefined}
      onClick={onTap ? () => { try { hapticTap(); } catch {} onTap(); } : undefined}
      style={{ margin: '8px 24px 0', display: 'flex', alignItems: 'center', gap: 10, cursor: onTap ? 'pointer' : 'default', WebkitTapHighlightColor: 'transparent' }}>
      <div style={{ fontFamily: FM, fontSize: 9, color: C.amber, textTransform: 'uppercase', letterSpacing: '1.4px', width: 44 }}>Carbs</div>
      <div style={{ flex: 1, position: 'relative', height: 5, borderRadius: 5, background: 'rgba(255,255,255,0.07)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${fill * 100}%`, background: C.amber, boxShadow: `0 0 6px ${C.amber}`, transition: 'width 1.3s ease', borderRadius: 5 }} />
        <div style={{ position: 'absolute', top: 0, bottom: 0, width: 30, background: 'linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)', animation: 'log-slide-shim 2.2s ease-in-out 1.5s infinite' }} />
      </div>
      <div style={{ fontFamily: FM, fontSize: 10, color: C.ink, fontWeight: 600, width: 70, textAlign: 'right' }}>
        {disp}<span style={{ fontSize: 8, color: C.faint }}>/{Math.round(max)}g</span>
      </div>
    </div>
  );
}

function LedgerBar({ pct, color, delay = 400, name, kcal, time, onTap, onRemove }) {
  const fill = useRingFill(Math.min(pct, 1), delay, 1200);
  const dispK = useCountUp(Math.round(kcal), 1000, delay);

  // Swipe-left-to-delete. Track horizontal drag, ignore vertical scrolls.
  const COMMIT = 90;          // distance past which release commits delete
  const REVEAL = 64;          // resting reveal width when held open
  const [dx, setDx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [removing, setRemoving] = useState(false);
  const startRef = useRef({ x: 0, y: 0, locked: null });
  // Track whether we've already buzzed for crossing the commit threshold
  // on the current drag, so we tick exactly once per swipe.
  const buzzedRef = useRef(false);

  const onTouchStart = (e) => {
    if (removing) return;
    const t = e.touches[0];
    startRef.current = { x: t.clientX, y: t.clientY, locked: null };
    buzzedRef.current = false;
    setAnimating(false);
  };
  const onTouchMove = (e) => {
    if (removing) return;
    const t = e.touches[0];
    const ddx = t.clientX - startRef.current.x;
    const ddy = t.clientY - startRef.current.y;
    // Lock direction on first decisive movement
    if (startRef.current.locked == null) {
      if (Math.abs(ddx) < 6 && Math.abs(ddy) < 6) return;
      startRef.current.locked = Math.abs(ddx) > Math.abs(ddy) ? 'h' : 'v';
    }
    if (startRef.current.locked !== 'h') return;
    // Only allow leftward drag (negative); cap rightward at 0.
    const next = Math.min(0, Math.max(-140, ddx));
    setDx(next);
    // Buzz once on first crossing of the commit threshold so the user
    // knows release-now would delete.
    if (!buzzedRef.current && -next > COMMIT) {
      buzzedRef.current = true;
      try { hapticHayahSwipe(); } catch {}
    }
  };
  const onTouchEnd = () => {
    if (removing) return;
    setAnimating(true);
    if (-dx > COMMIT) {
      // Slide off screen, then call onRemove.
      setRemoving(true);
      setDx(-360);
      try { hapticConfirm(); } catch {}
      setTimeout(() => { if (onRemove) onRemove(); }, 220);
      // Safety net: if parent doesn't unmount this row (e.g. onRemove
      // failed silently), spring it back to rest so the user isn't
      // stuck with a permanently-open red bar.
      setTimeout(() => { setRemoving(false); setDx(0); }, 700);
    } else if (-dx > 24) {
      setDx(-REVEAL);
    } else {
      setDx(0);
    }
  };
  const collapseIfOpen = () => {
    if (dx !== 0) { setAnimating(true); setDx(0); }
  };

  const transitionStyle = animating
    ? 'transform 0.22s cubic-bezier(0.22,0.61,0.36,1), opacity 0.22s ease'
    : 'none';

  return (
    <div style={{
      position: 'relative', display: 'flex', alignItems: 'center', gap: 10,
      marginBottom: 8, overflow: 'hidden', borderRadius: 10,
    }}>
      <div style={{ fontFamily: FM, fontSize: 10, color, width: 42, flexShrink: 0, opacity: 0.75 }}>{time}</div>

      {/* delete affordance behind the row */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 0, bottom: 0, right: 0,
        width: Math.max(REVEAL, -dx),
        background: -dx > COMMIT ? 'rgba(255,107,107,0.95)' : 'rgba(255,107,107,0.18)',
        transition: 'background 0.15s ease',
        pointerEvents: 'none',
        borderRadius: 10,
      }} />

      <div
        role={onTap ? 'button' : undefined}
        onClick={() => {
          // If the row is held open (revealed), tap closes it instead of opening edit.
          if (dx !== 0) { collapseIfOpen(); return; }
          try { hapticTap(); } catch {}
          if (onTap) onTap();
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
        style={{
          flex: 1, position: 'relative', height: 44, background: 'rgba(255,255,255,0.04)',
          borderRadius: 10, overflow: 'hidden', cursor: onTap ? 'pointer' : 'default',
          WebkitTapHighlightColor: 'transparent',
          transform: `translateX(${dx}px)`,
          transition: transitionStyle,
          opacity: removing ? 0 : 1,
          touchAction: 'pan-y',
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
        <div style={{
          position: 'absolute', top: 0, left: 0, bottom: 0,
          width: `${fill * 100}%`,
          background: `${color}1a`,
          transition: 'width 1.2s cubic-bezier(0.22,0.61,0.36,1)',
        }}>
          <div style={{
            position: 'absolute', top: 0, bottom: 0, right: 0, width: 4,
            background: color, boxShadow: `0 0 10px 2px ${color}`,
            borderRadius: '0 2px 2px 0',
          }} />
        </div>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', padding: '0 14px', justifyContent: 'space-between', zIndex: 1, gap: 10 }}>
          <span style={{ fontFamily: FS, fontSize: 14, color: C.ink, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
          <span style={{ fontFamily: FM, fontSize: 12, color: C.amber, fontWeight: 600, flexShrink: 0 }}>{dispK}</span>
        </div>
      </div>
    </div>
  );
}

// Slot constants must match MEAL_SLOTS in CalorieCounter.js. The legacy
// breakfast/lunch/dinner keys are no longer real slots — items routed to
// them appear in the flat ledger but are invisible in the Meals timeline.
const SLOT_COLORS = {
  meal:        C.amber,
  drinks:      '#7CC5F3',
  whey:        '#4FD9C2',
  supplements: '#b88df8',
  snacks:      '#82c83c',
};
const SLOT_LABELS = {
  meal: 'Meal', drinks: 'Drinks', whey: 'Whey Protein', supplements: 'Supplements', snacks: 'Junk Food',
};
const SLOT_ORDER = ['meal', 'drinks', 'whey', 'supplements', 'snacks'];

// Quick Log always lands new items in the 'meal' slot. addFoodEntry()
// in CalorieCounter handles the whey/junk auto-routing — Quick Log no
// longer needs a time-of-day heuristic, which was returning legacy keys
// (breakfast/lunch/dinner) that aren't real MEAL_SLOTS keys.
function autoSlot() {
  return 'meal';
}

function fmtTime(iso) {
  if (!iso) return '--:--';
  try {
    const d = new Date(iso);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  } catch { return '--:--'; }
}

function MacroFocusOverlay({ macroKey, value, max, color, todayData, open, onClose }) {
  const [phase, setPhase] = useState('closed');
  const [dragY, setDragY] = useState(0);
  const dragRef = useRef({ startY: 0, active: false });

  useEffect(() => {
    if (open) {
      setPhase('mounting');
      const t = setTimeout(() => setPhase('open'), 20);
      return () => clearTimeout(t);
    }
  }, [open]);

  const handleClose = () => {
    setPhase('closing');
    setTimeout(() => { setDragY(0); setPhase('closed'); onClose(); }, 360);
  };

  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open && phase === 'closed') return null;

  const label = { carbs: 'Carbs', fat: 'Fat' }[macroKey] || macroKey;
  const visible = phase === 'open';

  const mealBreakdown = SLOT_ORDER
    .map(slotKey => {
      const entries = todayData?.meals?.[slotKey] || [];
      const val = entries.reduce((s, e) => s + (e[macroKey] || 0), 0);
      return {
        slot: slotKey,
        label: SLOT_LABELS[slotKey] || slotKey,
        color: SLOT_COLORS[slotKey] || C.green,
        val,
        entries: entries.filter(e => (e[macroKey] || 0) > 0.05)
          .sort((a, b) => (b[macroKey] || 0) - (a[macroKey] || 0)),
      };
    })
    .filter(m => m.val > 0.05);

  const over = value > max;

  return createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget) handleClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: `rgba(3,9,6,${visible ? 0.94 : 0})`,
        transition: 'background 0.36s ease',
        overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      }}
      onTouchStart={e => { dragRef.current = { startY: e.touches[0].clientY, active: true }; }}
      onTouchMove={e => {
        if (!dragRef.current.active) return;
        const dy = Math.max(0, e.touches[0].clientY - dragRef.current.startY);
        setDragY(dy);
      }}
      onTouchEnd={() => {
        dragRef.current.active = false;
        if (dragY > 100) handleClose(); else setDragY(0);
      }}
    >
      <div style={{
        minHeight: '100%',
        padding: 'calc(20px + env(safe-area-inset-top)) 18px calc(28px + env(safe-area-inset-bottom))',
        opacity: visible ? 1 : 0,
        transform: `translateY(${visible ? dragY : 28}px)`,
        transition: phase === 'closing'
          ? 'opacity 0.36s ease, transform 0.36s ease'
          : dragY === 0 ? 'opacity 0.25s ease, transform 0.25s ease' : 'none',
      }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 8, height: 8, borderRadius: 8, background: color, boxShadow: `0 0 10px ${color}` }} />
            <span style={{ fontFamily: FM, fontSize: 11.5, color, letterSpacing: '1.8px', textTransform: 'uppercase', fontWeight: 600 }}>
              {label} · Breakdown
            </span>
          </div>
          <button onClick={handleClose} aria-label="Close" style={{
            width: 36, height: 36, borderRadius: 18,
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.14)',
            color: C.ink, cursor: 'pointer',
            display: 'grid', placeItems: 'center',
            fontSize: 20, lineHeight: 1, padding: 0,
          }}>×</button>
        </div>

        {/* Ring */}
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <GlowRing
            value={value} max={max} color={over ? '#ff6b6b' : color}
            size={210} sw={13} uid={`mf-${macroKey}`} delay={80}
            centerNode={
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FM, fontSize: 9.5, color: C.faint, letterSpacing: '1.8px', textTransform: 'uppercase' }}>Logged</div>
                <div style={{ fontFamily: FM, fontSize: 44, color: over ? '#ff6b6b' : color, fontWeight: 600, letterSpacing: '-1.2px', lineHeight: 1, marginTop: 4 }}>
                  {Math.round(value)}
                </div>
                <div style={{ fontFamily: FM, fontSize: 10, color: C.dim, letterSpacing: '0.8px', marginTop: 5 }}>
                  {Math.round((value / Math.max(1, max)) * 100)}% <span style={{ color: C.faint }}>of {max}g</span>
                </div>
              </div>
            }
          />
        </div>

        {/* Separator */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.07)', marginBottom: 20 }} />

        {/* Meal breakdown */}
        {mealBreakdown.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '28px 0', color: C.faint, fontFamily: FM, fontSize: 11, letterSpacing: '0.8px' }}>
            Nothing logged yet today.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {mealBreakdown.map(meal => (
              <div key={meal.slot}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 7, height: 7, borderRadius: 7, background: meal.color, flexShrink: 0 }} />
                  <span style={{ fontFamily: FM, fontSize: 10, color: C.ink, fontWeight: 600, flex: 1, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{meal.label}</span>
                  <span style={{ fontFamily: FM, fontSize: 12, color, fontWeight: 600 }}>{Math.round(meal.val)}g</span>
                  <span style={{ fontFamily: FM, fontSize: 9, color: C.faint }}>{Math.round((meal.val / Math.max(1, max)) * 100)}%</span>
                </div>
                <div style={{ height: 3, background: 'rgba(255,255,255,0.07)', borderRadius: 3, marginBottom: 7, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(100, (meal.val / Math.max(1, max)) * 100)}%`, background: meal.color, borderRadius: 3, transition: 'width 1.1s cubic-bezier(0.22,0.61,0.36,1)' }} />
                </div>
                {meal.entries.slice(0, 3).map((e, i) => (
                  <div key={e.id || i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0 3px 15px' }}>
                    <span style={{ fontFamily: FS, fontSize: 11.5, color: C.dim, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.name}</span>
                    <span style={{ fontFamily: FM, fontSize: 10.5, color: C.faint, marginLeft: 12, flexShrink: 0 }}>{Math.round(e[macroKey] || 0)}g</span>
                  </div>
                ))}
                {meal.entries.length > 3 && (
                  <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, padding: '2px 0 0 15px' }}>
                    +{meal.entries.length - 3} more
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function GhidhaLogView({
  totalCalories = 0,
  totalProtein = 0,
  totalCarbs = 0,
  totalFat = 0,
  goals,
  trainingActive = false,
  todayData,
  startLogMealNow,
  startLogForSlot,
  openEditEntry,
  removeEntryWithUndo,
  onOpenProteinFocus,
  onOpenQuickAdd,
  onOpenCoach,
  kcalAvgs = null,
} = {}) {

  const [openedMacro, setOpenedMacro] = useState(null); // 'carbs' | 'fat' | null
  const latestRef = useRef(null); // newest ledger row — scroll target

  const intake = Math.round(totalCalories || 0);
  const intakeGoal = Math.max(1, Math.round(goals?.calories || 2000));
  const pct = Math.min(100, Math.round((intake / intakeGoal) * 100));
  const dispKcal = useCountUp(intake, 1500, 200);

  const proteinGoal = Math.max(1, Math.round(goals?.protein || 150));
  const carbsGoal = Math.max(1, Math.round(goals?.carbs || 225));
  const fatGoal = Math.max(1, Math.round(goals?.fat || 65));

  // Flatten today's entries across slots into a chronological ledger.
  // Preserve the full entry (P/C/F, servingG, etc.) so openEditEntry
  // gets the same source data the meal-timeline rows pass — without it
  // synthFood.protein/carbs/fat defaulted to 0 and edits cleared the
  // macros on save.
  const ledger = useMemo(() => {
    const meals = todayData?.meals || {};
    const entries = [];
    for (const slotKey of Object.keys(meals)) {
      for (const e of (meals[slotKey] || [])) {
        entries.push({
          slot: slotKey,
          entry: e,
          name: e.name,
          kcal: e.calories || 0,
          loggedAt: e.loggedAt,
        });
      }
    }
    entries.sort((a, b) => new Date(a.loggedAt || 0) - new Date(b.loggedAt || 0));
    return entries;
  }, [todayData]);

  const today = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  })();

  // When opened via "View log →" (from the AI Coach) we land at the top on the
  // ring — but the user wants to see the food that was just logged, which sits
  // at the BOTTOM of the ledger. If a fresh scroll-to-latest flag is set, scroll
  // the newest row into view once entries have settled, then clear the flag.
  useEffect(() => {
    let flagged = false;
    try {
      const ts = Number(localStorage.getItem("ghidhaScrollToLatest") || 0);
      flagged = ts > 0 && (Date.now() - ts) < 8000;
      if (ts) localStorage.removeItem("ghidhaScrollToLatest");
    } catch {}
    if (!flagged) return;
    const t = setTimeout(() => {
      try {
        if (latestRef.current && latestRef.current.scrollIntoView) {
          latestRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } catch {}
    }, 420);
    return () => clearTimeout(t);
    // run once on mount; ledger length feeds the ref target
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      padding: '14px 0 16px',
      background: 'radial-gradient(ellipse at 50% 0%, rgba(0,230,118,0.08), transparent 50%)',
      backgroundColor: 'transparent',
      fontFamily: FS,
    }}>
      <style>{`
        @keyframes log-bar-shimmer{0%,100%{opacity:0.8}50%{opacity:1}}
        @keyframes log-glow-pulse{0%,100%{opacity:0.5;height:4px}50%{opacity:1;height:8px}}
        @keyframes log-slide-shim{0%{transform:translateX(-30px)}100%{transform:translateX(calc(100% + 30px))}}
        @media (prefers-reduced-motion: reduce){
          .hayah-quicklog-btn{animation:none!important}
          .hayah-quicklog-btn::after{animation:none!important;display:none}
        }
      `}</style>

      {/* Rolling kcal averages (7 / 14 / 30 days) — includes today's
          running intake so the figures update live as the user logs.
          Each cell colored against the goal: ≤goal teal, >goal coral. */}
      {kcalAvgs && (kcalAvgs.a7 != null || kcalAvgs.a14 != null || kcalAvgs.a30 != null) && (
        <div style={{
          display: 'flex', justifyContent: 'space-around', alignItems: 'center',
          padding: '8px 14px 12px', margin: '0 14px 4px',
          background: 'rgba(255,255,255,0.025)',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 10, gap: 10,
        }}>
          {[
            { label: '7D AVG',  v: kcalAvgs.a7 },
            { label: '14D AVG', v: kcalAvgs.a14 },
            { label: '30D AVG', v: kcalAvgs.a30 },
          ].map((m, i) => {
            const tone = m.v == null
              ? C.faint
              : (kcalAvgs.goal && m.v > kcalAvgs.goal ? C.coral : C.green);
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                <span style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: '1.2px', textTransform: 'uppercase', opacity: 0.8 }}>{m.label}</span>
                <span style={{ fontFamily: FM, fontSize: 15, fontWeight: 800, color: tone, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
                  {m.v == null ? '—' : m.v.toLocaleString()}
                </span>
                <span style={{ fontFamily: FM, fontSize: 8, color: C.faint, opacity: 0.6 }}>kcal</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Dial + flanking bars */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '4px 12px 0' }}>
        <VertMacroBar
          label="Protein"
          value={Math.round(totalProtein || 0)}
          max={proteinGoal}
          color={C.cyan}
          barH={170}
          delay={400}
          unit="g"
          isHero={true}
          remaining={proteinGoal - (totalProtein || 0)}
          onTap={onOpenProteinFocus}
        />
        <div
          style={{
            flex: 1, display: 'flex', justifyContent: 'center',
            cursor: onOpenQuickAdd ? 'pointer' : 'default',
            WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
            borderRadius: '50%',
          }}
          onClick={onOpenQuickAdd ? () => { try { hapticTap(); } catch {} onOpenQuickAdd(); } : undefined}
          role={onOpenQuickAdd ? 'button' : undefined}
          tabIndex={onOpenQuickAdd ? 0 : undefined}
          aria-label={onOpenQuickAdd ? 'Open Quick Log' : undefined}
          onKeyDown={onOpenQuickAdd ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenQuickAdd(); } } : undefined}
        >
          <GlowRing
            value={intake}
            max={intakeGoal}
            color={C.green}
            size={210}
            sw={13}
            delay={200}
            uid="ghidha-log-main"
            centerNode={
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: FM, fontSize: 9, color: C.faint, letterSpacing: '1.8px', textTransform: 'uppercase' }}>LOGGED</div>
                <div style={{ fontFamily: FS, fontSize: 46, fontWeight: 200, color: C.ink, letterSpacing: '-2px', lineHeight: 1, marginTop: 4 }}>
                  {dispKcal.toLocaleString()}
                </div>
                <div style={{ fontFamily: FM, fontSize: 10, color: C.dim, letterSpacing: '0.8px', marginTop: 5 }}>
                  {pct}% <span style={{ color: C.faint }}>of {intakeGoal.toLocaleString()}</span>
                </div>
                {trainingActive && (
                  <div style={{ display:'inline-flex', alignItems:'center', gap:4, marginTop:6,
                                padding:'3px 8px', borderRadius:999,
                                background:'rgba(0,201,167,0.16)', border:'0.5px solid rgba(0,201,167,0.55)' }}>
                    <span style={{ fontSize:10, lineHeight:1 }}>⚡</span>
                    <span style={{ fontFamily: FM, fontSize:8.5, color:'#00c9a7', fontWeight:800, letterSpacing:'1.2px', textTransform:'uppercase', lineHeight:1 }}>Training-day target</span>
                  </div>
                )}
                <div style={{ marginTop: 10, width: 80, height: 2, background: 'rgba(255,255,255,0.1)', borderRadius: 2, margin: '10px auto 0', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: C.green, boxShadow: `0 0 6px ${C.green}`, transition: 'width 1.5s ease' }} />
                </div>
                {onOpenQuickAdd && (
                  <div style={{ fontFamily: FM, fontSize: 8.5, color: C.faint, letterSpacing: '1.4px', marginTop: 8, opacity: 0.7 }}>🔍 TAP TO LOG FOOD</div>
                )}
                {onOpenCoach && (
                  <button
                    onClick={(e) => { e.stopPropagation(); try { hapticTap(); } catch {} onOpenCoach(); }}
                    style={{ marginTop: 9, display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '5px 11px', borderRadius: 999,
                      background: 'rgba(0,201,167,0.14)', border: '0.5px solid rgba(0,201,167,0.5)',
                      color: '#00c9a7', fontFamily: FM, fontSize: 8.5, fontWeight: 800,
                      letterSpacing: '1px', textTransform: 'uppercase', cursor: 'pointer' }}>
                    ✦ Log with AI
                  </button>
                )}
              </div>
            }
          />
        </div>
        <VertMacroBar
          label="Fat"
          value={Math.round(totalFat || 0)}
          max={fatGoal}
          color={C.coral}
          barH={170}
          delay={550}
          unit="g"
          onTap={() => setOpenedMacro('fat')}
        />
      </div>

      <CarbsBar value={Math.round(totalCarbs || 0)} max={carbsGoal} onTap={() => setOpenedMacro('carbs')} />

      {/* Intake Ledger */}
      <div style={{ padding: '14px 14px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{ width: 14, height: 1, background: C.green, opacity: 0.5 }} />
          <span style={{ fontFamily: FM, fontSize: 9.5, color: C.green, letterSpacing: '1.8px', textTransform: 'uppercase', opacity: 0.85 }}>INTAKE LEDGER</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
          <span style={{ fontFamily: FM, fontSize: 9, color: C.faint }}>{intake.toLocaleString()} kcal</span>
        </div>

        {ledger.length === 0 ? (
          <div style={{
            padding: '22px 14px', textAlign: 'center',
            border: '1px dashed rgba(255,255,255,0.1)', borderRadius: 12,
            color: C.faint, fontFamily: FM, fontSize: 11, letterSpacing: '0.8px',
          }}>
            Nothing logged yet today. Tap below to start.
          </div>
        ) : (
          ledger.map((item, i) => {
            const isLast = i === ledger.length - 1;
            const bar = (
              <LedgerBar
                key={item.entry?.id || i}
                pct={(item.kcal || 0) / Math.max(intake, 1)}
                color={SLOT_COLORS[item.slot] || C.green}
                delay={300 + i * 60}
                name={item.name}
                kcal={item.kcal}
                time={fmtTime(item.loggedAt)}
                onTap={() => openEditEntry && openEditEntry(item.slot, item.entry, today)}
                onRemove={() => removeEntryWithUndo && removeEntryWithUndo(item.slot, item.entry, today)}
              />
            );
            return isLast
              ? <div key={item.entry?.id || i} ref={latestRef}>{bar}</div>
              : bar;
          })
        )}

        <button
          onClick={() => {
            try { hapticConfirm(); } catch {}
            if (startLogForSlot) startLogForSlot(autoSlot());
            else if (startLogMealNow) startLogMealNow();
          }}
          className="hayah-quicklog-btn"
          style={{
            width: '100%', padding: '14px 18px', borderRadius: 16,
            background: `linear-gradient(135deg, ${C.green} 0%, #00b853 100%)`,
            border: 'none',
            color: '#03110a', fontFamily: FS,
            cursor: 'pointer', marginTop: 14,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            boxShadow: `0 6px 22px rgba(0,230,118,0.35), 0 0 0 1px rgba(0,230,118,0.5) inset, 0 -2px 0 rgba(0,0,0,0.18) inset`,
            position: 'relative', overflow: 'hidden',
          }}>
          <style>{`
            @keyframes hayah-ql-shine{0%{transform:translateX(-120%)}60%,100%{transform:translateX(220%)}}
            @keyframes hayah-ql-pulse{0%,100%{box-shadow:0 6px 22px rgba(0,230,118,0.35), 0 0 0 1px rgba(0,230,118,0.5) inset, 0 -2px 0 rgba(0,0,0,0.18) inset}50%{box-shadow:0 8px 30px rgba(0,230,118,0.55), 0 0 0 1px rgba(0,230,118,0.7) inset, 0 -2px 0 rgba(0,0,0,0.18) inset}}
            .hayah-quicklog-btn{animation:hayah-ql-pulse 3s ease-in-out infinite}
            .hayah-quicklog-btn:active{transform:scale(0.985);transition:transform 0.08s ease}
            .hayah-quicklog-btn::after{content:"";position:absolute;top:0;bottom:0;left:0;width:40%;background:linear-gradient(90deg,transparent 0%,rgba(255,255,255,0.45) 50%,transparent 100%);animation:hayah-ql-shine 3.4s ease-in-out 1.2s infinite;pointer-events:none}
          `}</style>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, position: 'relative', zIndex: 1 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 18,
              background: 'rgba(3,17,10,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 20, fontWeight: 700, lineHeight: 1,
              boxShadow: 'inset 0 0 0 1px rgba(3,17,10,0.22)',
              flexShrink: 0,
            }}>+</div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px', lineHeight: 1.1 }}>Quick Log</span>
              <span style={{ fontFamily: FM, fontSize: 9.5, letterSpacing: '0.6px', color: 'rgba(3,17,10,0.7)', fontWeight: 500 }}>
                auto-classified by food
              </span>
            </div>
          </div>
        </button>
      </div>

      <MacroFocusOverlay
        macroKey="carbs"
        value={Math.round(totalCarbs || 0)}
        max={carbsGoal}
        color={C.amber}
        todayData={todayData}
        open={openedMacro === 'carbs'}
        onClose={() => setOpenedMacro(null)}
      />
      <MacroFocusOverlay
        macroKey="fat"
        value={Math.round(totalFat || 0)}
        max={fatGoal}
        color={C.coral}
        todayData={todayData}
        open={openedMacro === 'fat'}
        onClose={() => setOpenedMacro(null)}
      />
    </div>
  );
}
